import { ComponentType, lazy } from "react";

const RELOADED_INTO_KEY = "preloadErrorReloadedInto";
const MAIN_SCRIPT_SELECTOR = 'script[type="module"][src*="/assets/index-"]';

const fetchDeployedMainScript = async () => {
  try {
    const response = await fetch("/", { cache: "no-store" });
    if (!response.ok) {
      return null;
    }

    const html = await response.text();

    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(MAIN_SCRIPT_SELECTOR)
      ?.getAttribute("src");
  } catch {
    return null;
  }
};

// After a deploy, an open tab asks for chunk files that no longer exist, and a reload gets the new ones.
// The main script name in the served index.html changes with each deploy. Reloading at most once into
// each one stops a loop when the reload does not help.
const reloadIntoDeployedVersion = async () => {
  const deployed = await fetchDeployedMainScript();
  if (!deployed || sessionStorage.getItem(RELOADED_INTO_KEY) === deployed) {
    return false;
  }

  sessionStorage.setItem(RELOADED_INTO_KEY, deployed);
  window.location.reload();

  return true;
};

export const reloadOnFailedLoad =
  <T>(load: () => Promise<T>) =>
  () =>
    load().catch(async (error: unknown) => {
      if (await reloadIntoDeployedVersion()) {
        // Stay pending, so the current page stays on screen until the reload replaces it.
        return new Promise<never>(() => {});
      }

      throw error;
    });

// Only route pages reload when their chunk fails. Other on-demand imports (Mermaid, exceljs, Stoplight)
// handle the failure in place, so the user does not lose a chat or a form.
export const lazyRoutePage = <P extends object>(
  load: () => Promise<{ default: ComponentType<P> }>
) => lazy(reloadOnFailedLoad(load));
